import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sp5pt8o1vn02ekujcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = (parsed as { meta?: { message?: string; detail?: string }; message?: string }).meta?.message
          || (parsed as { meta?: { message?: string; detail?: string }; message?: string }).meta?.detail
          || (parsed as { message?: string }).message
          || text
          || '提交失敗，請稍後再試。';
        setFormStatus('error');
        setFormError(String(msg));
      }
    } catch {
      setFormStatus('error');
      setFormError('網絡錯誤，請檢查連接後重試。');
    }
  };

  const milestones = [
    { year: '2016', title: '研發立項', description: '資深草本研究團隊匯集，開啟性福寶配方研發之旅' },
    { year: '2018', title: '配方確立', description: '經過數千次實驗，確定最終天然精華配方' },
    { year: '2020', title: '產品上市', description: '性福寶經典系列正式面向市場，獲得首批用戶的好評' },
    { year: '2022', title: '系列擴展', description: '推出尊享系列與伴侶系列，滿足不同用戶需求' },
    { year: '2024', title: '品質升級', description: '引進先進萃取技術，全面升級產品品質標準' },
    { year: '2026', title: '百萬用戶', description: '累計服務超過50,000名用戶，好評率達98%' },
  ];

  const certifications = [
    { icon: 'ri-shield-check-line', label: 'GMP 認證', description: '符合藥品生產質量管理規範' },
    { icon: 'ri-test-tube-line', label: '第三方檢測', description: '每批次產品經第三方獨立檢測' },
    { icon: 'ri-leaf-line', label: '天然原料', description: '精選非轉基因天然草本原料' },
    { icon: 'ri-award-line', label: '品質獎項', description: '榮獲多項行業品質認證與獎項' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="關於我們 | 性福寶——品牌故事與聯絡方式"
        description="了解性福寶的品牌故事、研發實力與品質理念。我們致力於以天然草本精華配方，幫助提升活力與自信。提供聯繫方式，隨時為您服務。"
        keywords="性福寶品牌,性福寶官方網站,性福寶聯繫方式,性福寶正品購買"
        canonicalUrl="https://xingfubao.com/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'name': '關於性福寶',
          'description': '了解性福寶的品牌故事、研發實力與品質理念。',
          'url': 'https://xingfubao.com/about',
        }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首頁', href: '/' },
              { label: '關於我們' },
            ]}
          />
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                關於性福寶
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                融合千年傳統草本智慧與現代科技，為追求高品質生活的人們提供值得信賴的活力方案
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center mb-16">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    性福寶的誕生源於一個簡單而深刻的信念——<strong>每個人都值得擁有充滿活力的幸福生活</strong>。
                    我們的創始團隊在草本研究領域深耕多年，深感現代人因生活壓力、作息紊亂導致的身體亞健康狀態，
                    決心打造一款真正有效、安全可靠的天然活力產品。
                  </p>
                  <p>
                    經過十年的不懈研發，團隊深入研究《本草綱目》等古典醫籍，並結合現代藥理學研究，
                    從上百種草本原料中篩選出最具潛力的核心成分。通過先進的超臨界CO₂萃取技術和科學配比，
                    終於打造出<strong>性福寶獨特的天然精華配方</strong>。
                  </p>
                  <p>
                    自上市以來，性福寶已累計服務超過50,000名用戶，獲得了98%的好評率。
                    我們始終堅持以品質為核心，以用戶滿意為目標，不斷優化產品配方與服務體驗。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20modern%20laboratory%20with%20natural%20herbs%20and%20glassware%20on%20wooden%20table%2C%20warm%20ambient%20lighting%2C%20scholarly%20yet%20contemporary%20wellness%20research%20setting%2C%20clean%20composition&width=800&height=600&seq=xfb-about-brand&orientation=landscape"
                  alt="性福寶研發實驗室"
                  title="性福寶品牌故事 - 研發實驗室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div id="research" className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                品質認證
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {certifications.map((cert, idx) => (
                  <div key={idx} className="bg-background-100/70 rounded-lg p-5 text-center cursor-default">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                      <i className={`${cert.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-medium text-sm text-foreground-900 mb-1">{cert.label}</h3>
                    <p className="text-xs text-foreground-500">{cert.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                發展歷程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200/70 md:-translate-x-px"></div>
                <div className="space-y-10">
                  {milestones.map((m, idx) => (
                    <div key={idx} className={`relative flex items-start gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 md:text-right md:pr-8 hidden md:block">
                        {idx % 2 === 0 ? (
                          <div className="bg-background-100/70 rounded-lg p-4 inline-block text-left">
                            <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{m.title}</h4>
                            <p className="text-xs text-foreground-600">{m.description}</p>
                          </div>
                        ) : null}
                      </div>
                      <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center -translate-x-1/2 z-10">
                        <span className="text-xs font-bold">{String(idx + 1)}</span>
                      </div>
                      <div className="flex-1 pl-12 md:pl-0 md:pr-8">
                        <span className="text-xs text-primary-600 font-medium">{m.year}</span>
                        <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1 mt-0.5">{m.title}</h4>
                        <p className="text-xs text-foreground-600 md:hidden">{m.description}</p>
                      </div>
                      <div className="flex-1 md:pl-8 hidden md:block">
                        {idx % 2 !== 0 ? (
                          <div className="bg-background-100/70 rounded-lg p-4 inline-block">
                            <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{m.title}</h4>
                            <p className="text-xs text-foreground-600">{m.description}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div id="contact" className="bg-background-100/60 rounded-lg p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    聯繫我們
                  </h2>
                  <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                    如有任何疑問或需要專業諮詢，歡迎透過以下方式聯繫我們，性福寶健康顧問團隊將竭誠為您服務。
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-0.5">客服電話</h4>
                        <p className="text-sm text-foreground-600">400-888-8888</p>
                        <p className="text-xs text-foreground-400">週一至週五 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-0.5">電子郵箱</h4>
                        <p className="text-sm text-foreground-600">service@xingfubao.com</p>
                        <p className="text-xs text-foreground-400">將在24小時內回覆</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-0.5">官方微信</h4>
                        <p className="text-sm text-foreground-600">xingfubao_official</p>
                        <p className="text-xs text-foreground-400">掃碼關注獲取最新資訊</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">發送訊息</h3>
                  <form
                    id="contact-form"
                    data-readdy-form="true"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 text-foreground-900 text-sm border border-background-200/70 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        placeholder="請輸入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">電子郵箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 text-foreground-900 text-sm border border-background-200/70 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        placeholder="請輸入您的電子郵箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">訊息內容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 text-foreground-900 text-sm border border-background-200/70 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                        placeholder="請輸入您的訊息（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <div className="honeypot-guard">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 text-background-50 py-3 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      發送訊息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">感謝您的訊息！我們將盡快回覆。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default AboutPage;